/**
 * RemoteCastVFXHandler — everything OTHER clients' actions draw on this
 * screen, extracted from VFXSystem (decomposition arc, 2026-06-12).
 *
 *  - EVENTS.ABILITY_CASTED      → remote cast glow + AoE/ground/aura shapes
 *                                 (Lane 5 parity — resolveRemoteAbilityVfx)
 *  - EVENTS.REMOTE_DASHED       → Charge/Heroic Leap trail + landing beat
 *  - EVENTS.NPC_DEFEATED        → fed to KillBeatDirector (single kill-beat
 *                                 owner: smoke/sparkle, boss finisher, XP text)
 *  - net:message:debugCombat    → server combat-audit → DebugFCT rows
 *
 * Remote level-ups live in LevelUpVFXHandler (one funnel, local + remote).
 *
 * Parity rule: remote plays route through the SAME spawn funnel + effect-id
 * resolution the local caster uses, so both screens draw the same pixels.
 */
import { gameLogger } from '../../../utils/GameLogger';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import { DebugFCT } from '../../debug/DebugFCT';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { VisualLifecycleStore } from '../../diagnostics/VisualLifecycleStore';
import type { VFXEffectPlayer } from '../VFXEffectPlayer';
import { resolveRemoteAbilityVfx, resolveRemoteCastGlowId } from '../RemoteAbilityVFX';
import { ABILITY_DATA } from '../../../shared/content/generated/abilities.gen';
import { findPlayerPosition, localSessionId } from './entityPositions';
import { KillBeatDirector } from './KillBeatDirector';
import { REMOTE_VFX_ORIGIN } from '../screenFeelScope';
import { RemoteDashVFX, type RemoteDashReceipt, type RemoteDashPose } from './RemoteDashVFX';
import { ThrowingStarReloadVFX } from './ThrowingStarReloadVFX';
import { isThrowingStarAbility } from '../../../shared/combat/throwingStars';
import { bootSearchParams } from '../../../config/boot/bootUrl';
import { RemoteCastGeometryPlayer } from './RemoteCastGeometryPlayer';
import { resolveCastGeometry, hasRemoteVisuals, geometryEffectIds, type CastGeometry } from '../castGeometry/castGeometry';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { playerCombatPoint } from '../../../shared/logic/CombatAnchors';
import { resolveProjectileLaunch, resolveTargetBody } from '../../systems/projectile/ProjectileAimResolver';
import { RemoteProjectileRelease, remoteProjectileCastKey, remoteProjectileScopeKey, sharesInstantProjectileRelease } from '../castGeometry/remoteProjectileRelease';
import { StealthVeil } from './StealthVeil';
import { getMotionPrefs } from '../../systems/accessibility/motionPrefs';
import { clearActiveCastWindups } from '../../systems/combat/SequencedCastWindup';

/**
 * TEMP dev-gated diagnostic for the remote-AoE-VFX path (L5). Records the last
 * N stages onRemoteAbilityCasted reaches on an observer client so a 2-client
 * probe can see exactly where the path stops (event → entitysystem → sprite →
 * plays). Only writes when `?testhooks=1`/`?vqa=1` is present (same gate as the
 * render-truth hook); never allocates in prod. Read via window.__VFX_REMOTE_DIAG__.
 */
function remoteVfxDiag(stage: string, info: Record<string, unknown>): void {
  try {
    if (typeof window === 'undefined') return;
    const sp = bootSearchParams();
    if (sp.get('testhooks') !== '1' && sp.get('vqa') !== '1') return;
    const w = window as any;
    const log: any[] = w.__VFX_REMOTE_DIAG__ ?? (w.__VFX_REMOTE_DIAG__ = []);
    log.push({ stage, ...info, t: Date.now() });
    if (log.length > 200) log.shift();
    // DEBUG: also mirror to __ABILITY_CASTED_DEBUG__ so we can compare why
    // remoteCastDiagnostics stays empty while abilityCastedDebug has entries.
    try {
      const a: any[] = w.__ABILITY_CASTED_DEBUG__ ?? (w.__ABILITY_CASTED_DEBUG__ = []);
      a.push({ stage: `vfx_diag:${stage}`, ...info, t: Date.now() });
      if (a.length > 200) a.shift();
    } catch { /* nop */ }
  } catch {
    /* never throw into the render path */
  }
}

export interface RemoteCastVFXDeps {
  effectPlayer: VFXEffectPlayer;
  /** The canonical data-driven spawn funnel (VFXSystem.spawnEffectFromData). */
  spawnEffectFromData: (data: { effectId: string; x: number; y: number; targetId?: string; abilityId?: string; [key: string]: any }) => void;
  /** Legacy particle fallback (VFXSystem.spawnEffect). */
  spawnEffect: (effectId: string, x: number, y: number) => void;
}

export class RemoteCastVFXHandler {
  private projectileRelease = this.createProjectileRelease();
  private readonly remoteDash: RemoteDashVFX;
  private remoteAuraTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private throwingStarReload = new ThrowingStarReloadVFX();
  // Plan 29 F5: swings, areas, zones, cones, auras and orbits drawn from the
  // ability's cast geometry — the plan the caster's own behaviours emit.
  private geometryPlayer = new RemoteCastGeometryPlayer({
    cameraView: () => PixiOverlayHost.getCameraView(),
    targetBody: (id) => resolveTargetBody(id),
    currentCaster: (id) => {
      try { return ServiceRegistry.getInstance().get('EntitySystem', { optional: true })?.getRemotePlayerManager?.()?.getOtherPlayers?.()?.get?.(id); }
      catch { return null; }
    },
    casterIdentity: id => {
      const actor = this.releaseActor(id);
      return actor && !actor.isDead && actor.isOnline !== false && (actor.health === undefined || actor.health > 0) ? actor.identity : null;
    },
    launch: (projectile) => {
      try {
        ServiceRegistry.getInstance().get('EntitySystem')?.getProjectileSystem?.()?.createProjectile?.(projectile as never);
      } catch { /* VFX is non-critical */ }
    },
  });

  // Plan 29 F9 (lane BY): a stealthed player fades — on their own screen (the
  // local cast) and on every observer's (the broadcast).
  private stealthVeil = new StealthVeil({
    spriteFor: (id, local) => {
      const es = ServiceRegistry.getInstance().get('EntitySystem') as any;
      const sprite = local ? es?.getPlayerRenderer?.()?.getPlayerSprite?.() : es?.getRemotePlayerManager?.()?.getSprite?.(id);
      return typeof sprite?.setAlpha === 'function' ? sprite : null;
    },
    reducedMotion: () => getMotionPrefs().reduceMotion,
  });

  /**
   * The one funnel every remote-cast play goes through (glow, AoE/ground/
   * aura shapes, dash trail + landing, throwing-star beats). It stamps
   * `origin: 'remote'`, so the caster's world shapes draw here but the def's
   * screen shake/flash/tint stay on the caster's own screen (screenFeelScope).
   * Kill beats (KillBeatDirector) are not casts and keep the raw deps.
   */
  private readonly remote: {
    playById: (effectId: string, x: number, y: number, overrides?: Record<string, unknown>) => void;
    spawn: (data: Parameters<RemoteCastVFXDeps['spawnEffectFromData']>[0]) => void;
  };

  constructor(private deps: RemoteCastVFXDeps) {
    this.remote = {
      playById: (effectId, x, y, overrides) =>
        void deps.effectPlayer.playById(effectId, x, y, { ...overrides, origin: REMOTE_VFX_ORIGIN }),
      spawn: (data) => deps.spawnEffectFromData({ ...data, origin: REMOTE_VFX_ORIGIN }),
    };
    this.remoteDash = new RemoteDashVFX({
      caster: id => this.remoteDashCaster(id), textureKey: id => this.remoteTextureKey(id),
      spawn: data => this.remote.spawn(data), instant: data => this.onRemoteInstantDash(data),
      arrival: data => this.playArrivalSwing(data),
    });
  }

  private createProjectileRelease(): RemoteProjectileRelease {
    return new RemoteProjectileRelease({ caster: id => this.releaseActor(id),
      emit: ({ castKey: _key, scopeKey: _scope, clientCastSeq: _seq, targetId: _target, ...cue }) =>
        EventBus.getInstance().emit('vfx:spawn', { ...cue, follow: false, origin: REMOTE_VFX_ORIGIN }) });
  }

  /** Install subscriptions via the owner's tracked `sub` helper. */
  register(sub: (event: string, handler: (data: any) => void) => void): void {
    this.projectileRelease.dispose();
    const release = this.projectileRelease = this.createProjectileRelease();
    // DEBUG
    try {
      const sp = bootSearchParams();
      if (sp.get('testhooks') === '1' || sp.get('vqa') === '1') {
        const w = window as any;
        const log: any[] = w.__ABILITY_CASTED_DEBUG__ ?? (w.__ABILITY_CASTED_DEBUG__ = []);
        log.push({ stage: 'remote_handler_register_top', t: Date.now() });
        if (log.length > 200) log.shift();
      }
    } catch { /* nop */ }
    // Lane 5 (remote-AoE-VFX parity): the server broadcasts each cast as the
    // `abilityCasted` message, which NetworkManager re-emits ONLY as the
    // canonical EVENTS.ABILITY_CASTED ('combat:abilityCasted'). Subscribing
    // to the old phantom `net:message:abilityCasted` left this entire
    // remote-AoE-VFX path as dead code — observer B drew nothing.
    remoteVfxDiag('register', {});
    sub(EVENTS.ABILITY_CASTED, (data) => { if (this.projectileRelease === release) this.onRemoteAbilityCasted(data); });
    sub(EVENTS.VFX_REMOTE_PROJECTILE_RELEASE, data => release.release(data));
    sub(EVENTS.VFX_STOP_INSTANCE, data => { if (this.projectileRelease === release && typeof data?.instanceId === 'string') this.deps.effectPlayer.stopPersistentEffect(data.instanceId); });
    sub(EVENTS.COMBAT_CAST_CANCELLED, data => release.cancel(data?.casterId, data?.abilityId, data?.clientCastSeq));
    sub(EVENTS.AUDIO_ABILITY_CAST, (cast) => this.stealthVeil.onCast(cast?.casterId, cast?.abilityId, true));
    sub(EVENTS.NET_DISCONNECTED, () => { this.throwingStarReload.clear(); this.remoteDash.clear(); release.cancel(); });
    sub(EVENTS.LEVEL_UNLOADED, () => { this.remoteDash.clear(); release.cancel(); });
    sub(EVENTS.MAP_SERVER_SWITCH_START, () => { this.remoteDash.clear(); release.cancel(); });
    sub('net:remotePlayerLeave', data => { this.remoteDash.remove(data?.sessionId); release.cancel(data?.sessionId); });
    sub('net:message:playerTeleported', data => { const id = data?.sessionId ?? data?.playerId; this.remoteDash.cancel(id); release.cancel(id); });
    sub(EVENTS.WIRE_PLAYER_DIED, data => { const id = data?.victimSid ?? data?.id; this.remoteDash.cancel(id); release.cancel(id); });
    sub(EVENTS.WIRE_PLAYER_RESPAWNED, data => release.cancel(data?.victimSid));
    sub(EVENTS.WIRE_STATUS_APPLIED, data => {
      if (data?.data?.abilityStatus?.movement?.kind === 'root') this.remoteDash.cancel(data.targetId);
    });
    // Remote travel-mode dashes (Charge/Heroic Leap) — the `dashed` broadcast
    // carries abilityId + travelMs; NetworkManager re-emits non-local ones
    // here so other clients see the trail + landing, not a silent glide.
    sub(EVENTS.REMOTE_DASHED, (data) => this.onRemoteDashed(data));
    // Canonical NPC-defeat event (the old `net:message:npcDefeated` was never
    // emitted — smoke/sparkle/boss-finisher/XP text had been silently dead).
    sub(EVENTS.NPC_DEFEATED, (data) => this.onRemoteNPCDefeated(data));
    // K25: route the kill beat through the director's canonical spawn funnels
    // (same effect-id resolution both screens use — parity rule).
    KillBeatDirector.getInstance().install({
      spawnEffect: (effectId, x, y) => this.deps.spawnEffect(effectId, x, y),
      // Forward overrides — [mob.death] scale rides playById overrides; a
      // 3-arg closure silently dropped boss scale on this wired path.
      playById: (effectId, x, y, overrides) => this.deps.effectPlayer.playById(effectId, x, y, overrides),
    });
    sub('net:message:debugCombat', (data) => this.onServerDebugCombat(data));
  }

  /** Cancel all in-flight dash windows — called from VFXSystem teardown. */
  teardown(): void {
    clearActiveCastWindups();
    this.projectileRelease.dispose();
    this.throwingStarReload.clear();
    this.geometryPlayer.teardown();
    this.stealthVeil.teardown();
    for (const timer of this.remoteAuraTimers.values()) clearTimeout(timer);
    this.remoteAuraTimers.clear();
    this.remoteDash.clear();
  }

  // ── Remote travel-mode dash presentation ──────────────────────────

  /** Timed travel and witnessed airborne landings have one bounded owner. */
  private onRemoteDashed(data: RemoteDashReceipt): void {
    this.remoteDash.start(data);
  }
  /**
   * Another player's INSTANT dash (Blink, Disengage, Shadow Step, Shield
   * Charge) — plan 29 F5: the origin and destination puffs the caster's
   * DashBehavior draws (body centre, facing-flipped), then the swing a dash
   * carries at the landing. The origin is the remote body as this screen
   * still shows it (the schema snap follows the broadcast).
   */
  private onRemoteInstantDash(data: { sessionId: string; x: number; y: number; abilityId?: string }): void {
    if (data.sessionId === localSessionId()) return;
    const dash = resolveCastGeometry(data.abilityId)?.dash;
    const caster = this.remoteCaster(data.sessionId);
    if (dash && caster) {
      const flipX = caster.facing === 'left';
      if (dash.originEffectId) {
        this.remote.spawn({ effectId: dash.originEffectId, ...playerCombatPoint(caster), duration: 300, abilityId: data.abilityId, flipX, follow: false });
      }
      if (dash.destinationEffectId) {
        this.remote.spawn({ effectId: dash.destinationEffectId, ...playerCombatPoint({ x: data.x, y: data.y }), duration: 300, abilityId: data.abilityId, flipX, follow: false });
      }
    }
    this.playArrivalSwing(data);
  }

  private playArrivalSwing(data: { sessionId: string; x: number; y: number; abilityId?: string }): void {
    const caster = data.abilityId ? this.remoteCaster(data.sessionId) : null;
    if (caster) this.geometryPlayer.playArrival(data.abilityId!, data.sessionId, caster, { x: data.x, y: data.y });
  }

  private remoteCaster(sessionId: string): any | null {
    try {
      return ServiceRegistry.getInstance().get('EntitySystem')
        ?.getRemotePlayerManager?.()?.getOtherPlayers?.()?.get?.(sessionId) ?? null;
    } catch {
      return null;
    }
  }

  private releaseActor(sessionId: string) {
    const caster = this.remoteCaster(sessionId);
    if (!caster) return null;
    const rows = ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom?.()?.state?.players;
    const actor = rows?.get?.(sessionId) ?? (rows ? null : caster);
    return actor ? { identity: actor, health: actor.health, isDead: actor.isDead, isOnline: actor.isOnline } : null;
  }

  private queueProjectileRelease(data: any, geometry: CastGeometry | undefined, effectId: string): boolean {
    const g = geometry?.projectile;
    if (!g?.muzzle) return false;
    const same = sharesInstantProjectileRelease(geometry, effectId);
    const identity = { casterId: data.playerId, abilityId: data.ability_id ?? data.abilityId,
      scopeKey: remoteProjectileScopeKey(),
      castKey: remoteProjectileCastKey(data, data.playerId, data.ability_id ?? data.abilityId, data.client_cast_seq),
      clientCastSeq: typeof data.client_cast_seq === 'number' ? data.client_cast_seq : undefined };
    const fallback = same && !isThrowingStarAbility(identity.abilityId) ? () => {
      const caster = this.remoteCaster(identity.casterId);
      if (!caster) return null;
      const targetId = data?.target_id ?? data?.targetId;
      const launch = resolveProjectileLaunch({ caster, targetId, speed: g.speed, projectileWidth: g.width, projectileHeight: g.height });
      return { ...identity, ...g.muzzle!, effectId, x: launch.spawnX, y: launch.spawnY, targetId };
    } : undefined;
    this.projectileRelease.observeCast(identity, fallback, g.windUpMs);
    return same;
  }

  private remoteDashCaster(sessionId: string): RemoteDashPose | null {
    const caster = this.remoteCaster(sessionId);
    if (!caster) return null;
    try {
      const rows = ServiceRegistry.getInstance().get('NetworkManager', { optional: true })?.getRoom?.()?.state?.players;
      const schema = rows?.get?.(sessionId);
      if (rows && !schema) return null;
      if (!schema) return caster; // legacy adapters have no authority projection
      return { ...caster, health: schema.health, isDead: schema.isDead, isOnline: schema.isOnline,
        authority: { x: schema.x, y: schema.y, isGrounded: schema.isGrounded, velocityY: schema.velocityY } };
    } catch { return null; }
  }

  /** Remote player's sprite texture key (RemotePlayerManager delegate) —
   *  best-effort, null when the manager/renderer isn't ready. */
  private remoteTextureKey(sessionId: string): string | null {
    try {
      return ServiceRegistry.getInstance().get('EntitySystem')
        ?.getRemotePlayerManager?.()?.getTextureKey?.(sessionId) ?? null;
    } catch {
      return null;
    }
  }

  // ── Remote casts (glow + AoE/ground/aura shapes) ──────────────────

  private onRemoteAbilityCasted(data: any): void {
    const playerId = data?.playerId;
    const abilityId = data?.ability_id || data?.abilityId;
    const abilityName = data?.ability_name || data?.abilityName || abilityId;
    if (playerId && playerId === localSessionId()) return;
    remoteVfxDiag('call', { playerId, abilityId });
    this.stealthVeil.onCast(playerId, abilityId, false);
    // Fetch the remote player system or sprite directly to get coordinates
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
    if (!entitySystem) { remoteVfxDiag('no_entitysystem', { playerId }); return; }

    const remotePlayerManager = entitySystem.getRemotePlayerManager();
    if (!remotePlayerManager) { remoteVfxDiag('no_rpm', { playerId }); return; }

    // RemotePlayerManager exposes a public getSprite() (delegates to its private
    // renderer) — there is NO getRenderer() method, so the old
    // `getRenderer().getSprite()` threw a silent TypeError that aborted this
    // handler before any VFX spawned (L5 layer 3). Use the public accessor, the
    // same one the green chat-bubble path uses.
    const remoteSprite = remotePlayerManager.getSprite(playerId);
    if (!remoteSprite) { remoteVfxDiag('no_sprite', { playerId }); return; }
    remoteVfxDiag('sprite_ok', { playerId, x: remoteSprite.x, y: remoteSprite.y });
    for (const beat of this.throwingStarReload.observe(data, remoteSprite, Date.now())) {
      this.remote.spawn(beat);
    }

    // NOTE: the ability-NAME floating text is owned by RemotePlayerVfxBridge
    // (also on EVENTS.ABILITY_CASTED). We deliberately do NOT call
    // showAbilityName here — now that this handler shares that canonical event,
    // doing so would double-render the name. This handler owns the cast GLOW +
    // AoE/ground/aura SHAPES, which the bridge does not draw.
    void abilityName;

    // Lane 5 (VFX & Rendering Parity): AoE / ground / aura abilities broadcast
    // fine but used to draw nothing remotely. Resolve their VFX plays from the
    // ability's TOML metadata at the remote caster's position and route them
    // through spawnEffectFromData — the SAME player + effect-id resolution the
    // local caster uses (getAbilityVFX fallback included) → identical render.
    // Resolved BEFORE the glow so the glow's double-play guard can see them.
    const targetX = data?.target_x ?? data?.targetX;
    const targetY = data?.target_y ?? data?.targetY;
    // Plan 29 F5: when the ability's cast geometry draws something, it owns
    // the cast's shapes — placed, sized and faced as on the caster's screen —
    // and the content-only zone/impact replay at the caster stays out (it
    // drew Meteor on the caster at authored size). Buff auras keep their path.
    const geometry = resolveCastGeometry(abilityId);
    const remoteCaster = remotePlayerManager.getOtherPlayers?.()?.get?.(playerId);
    const geometryOwns = !!abilityId && !!remoteCaster && hasRemoteVisuals(geometry);
    if (geometryOwns) {
      const outcome = this.geometryPlayer.play(abilityId, playerId, remoteCaster, data?.target_id ?? data?.targetId, data?.client_cast_seq, data);
      remoteVfxDiag('geometry', { abilityId, outcome });
    }
    const geometryIds = new Set(geometryOwns ? geometryEffectIds(geometry) : []);
    // A projectile-only volley can be tagged AoE in content without drawing a
    // ground zone. Classify its unbanned cue before the legacy-play deduper.
    const intendedCastId = resolveRemoteCastGlowId(abilityId, data?.damageSchool);
    const delegatedReleaseId = !hasRemoteVisuals(geometry) && sharesInstantProjectileRelease(geometry, intendedCastId)
      ? intendedCastId : undefined;
    const remotePlays = (abilityId ? resolveRemoteAbilityVfx(abilityId, remoteSprite.x, remoteSprite.y, targetX, targetY) : [])
      .filter((p) => (!geometryOwns || (p.category === 'aura' && !geometryIds.has(p.effectId)))
        && (!delegatedReleaseId || p.category === 'aura')
        && p.effectId !== delegatedReleaseId);
    // Helpful wards snap around the server-selected player, as on the caster's
    // screen. An absent target means self; an unresolved explicit ally must
    // not redirect a ward onto the caster on this observer's screen.
    const ability = abilityId ? ABILITY_DATA[abilityId] : undefined;
    const wardTarget = ability?.target === 'friendly' && ability.abilityType === 'defensive'
      ? data?.target_id ?? data?.targetId : undefined;
    const wardAt = wardTarget ? findPlayerPosition(wardTarget) : null;
    const glowAt = wardAt ?? remoteSprite;
    const glowTargetId = wardAt ? wardTarget : playerId;

    // Use ability-specific cast VFX (above-head cast glow). Cast-side doctrine
    // flip (2026-07-17): resolveRemoteCastGlowId is TOML-first — authored
    // cast_effect → curated map castVfxId → archetype fallback → TOML-school
    // hit — matching the local wind-up chain (parity). Double-play guard
    // (doctrine §5 risk 1): when the zone/aura play above already draws the
    // authored cast_effect at this position, skip that tier so the same effect
    // doesn't render twice per remote cast.
    const authoredCastEffect = abilityId
      ? (ABILITY_DATA[abilityId] as { vfx?: { castEffect?: string } } | undefined)?.vfx?.castEffect
      : undefined;
    const authoredCoveredByPlays = !!authoredCastEffect
      && (remotePlays.some((p) => p.effectId === authoredCastEffect) || geometryIds.has(authoredCastEffect));
    const castEffectId = resolveRemoteCastGlowId(abilityId, data?.damageSchool, {
      omitAuthoredCastEffect: authoredCoveredByPlays,
    });
    const projectileOwnsRelease = this.queueProjectileRelease(data, geometry, castEffectId);
    // Residual-overlap guard (K27 remote half): the resolver bans the authored
    // id from its tiers, but the resolved glow can still collide with a NON-
    // authored play this cast already draws (aoe_impact / school-hit ids).
    // Never draw the same id twice per remote cast — and never record a
    // lifecycle row for a glow that did not render.
    if (isThrowingStarAbility(abilityId ?? '')) {
      remoteVfxDiag('projectile_owns_release', { abilityId });
    } else if (projectileOwnsRelease) {
      remoteVfxDiag('projectile_owns_release', { abilityId });
    } else if (wardTarget && !wardAt) {
      remoteVfxDiag('ward_recipient_unavailable', { abilityId, targetId: wardTarget });
    } else if (geometryOwns) {
      // Plan 29 F5: the geometry plan is everything the caster's screen draws
      // for this cast; a glow on top is a beat the caster never saw (a big
      // yellow flash over Fan of Knives on the observer's screen only).
      remoteVfxDiag('glow_geometry_owned', { abilityId, castEffectId });
    } else if (remotePlays.some((p) => p.effectId === castEffectId) || geometryIds.has(castEffectId)) {
      remoteVfxDiag('glow_covered', { abilityId, castEffectId });
    } else {
      VisualLifecycleStore.record({
        type: 'ability_cast_vfx',
        label: `${abilityId ?? '?'} → ${castEffectId} (remote)`,
        x: glowAt.x, y: glowAt.y,
        abilityId: abilityId ?? null,
        meta: { effectId: castEffectId, origin: 'remote', playerId },
      });
      this.remote.playById(castEffectId, glowAt.x, glowAt.y, { targetId: glowTargetId });
    }

    if (abilityId) {
      remoteVfxDiag('plays', { abilityId, count: remotePlays.length, ids: remotePlays.map((p) => p.effectId) });
      for (const play of remotePlays) {
        const spawn = () => this.remote.spawn({
          effectId: play.effectId,
          ...(play.category === 'aura' ? findPlayerPosition(playerId) ?? { x: play.x, y: play.y } : { x: play.x, y: play.y }),
          targetId: playerId,
          abilityId,
          ...play.overrides,
        });
        // Buff auras live outside the geometry plan, but share its wind-up.
        // Give them their full duration after it, and cancel pending scene work.
        const delay = play.category === 'aura' ? geometry?.castDelayMs ?? 0 : 0;
        if (delay > 0) {
          const key = `${playerId}:${abilityId}:${play.effectId}`;
          const previous = this.remoteAuraTimers.get(key);
          if (previous !== undefined) clearTimeout(previous);
          this.remoteAuraTimers.set(key, setTimeout(() => {
            this.remoteAuraTimers.delete(key);
            if (findPlayerPosition(playerId)) spawn();
          }, delay));
        } else spawn();
      }
    }
  }

  // ── Server combat audit + remote celebration beats ────────────────

  private onServerDebugCombat(data: any): void {
    const { reason, npcId, npcName, distance, effectiveDistance, maxRange, abilityId, damage, critical, oldHealth, newHealth } = data;
    switch (reason) {
      case 'hit_success':
        // Successful hit audit — log for debugging, don't show as failure
        gameLogger.debug('vfx', `[COMBAT_AUDIT] HIT ${npcName || npcId}: ${abilityId} → ${damage}dmg${critical ? ' CRIT' : ''} (${oldHealth}→${newHealth}HP) dist=${distance}px eff=${effectiveDistance}px max=${maxRange}px`);
        break;
      case 'out_of_range':
        DebugFCT.failPlayer('combat', `Server: Out of range — dist=${distance}px (effective=${effectiveDistance}px) max=${maxRange}px → ${npcId}`);
        break;
      case 'unknown_ability':
        DebugFCT.failPlayer('combat', `Server: Unknown ability "${abilityId}" — not in contracts or TOML`);
        break;
      case 'npc_not_found':
        DebugFCT.failPlayer('combat', `Server: NPC "${npcId}" not found — may have despawned or been defeated`);
        break;
      case 'cooldown':
        DebugFCT.failPlayer('combat', `Server: Ability "${abilityId}" on cooldown`);
        break;
      default:
        DebugFCT.failPlayer('combat', `Server reject: ${reason} ${npcId ? `(target: ${npcId})` : ''} ${abilityId ? `(ability: ${abilityId})` : ''}`.trim());
    }
  }

  private onRemoteNPCDefeated(data: any): void {
    // K25 single-owner consolidation: the kill beat (smoke/sparkle, boss
    // finisher, +XP text) is owned by KillBeatDirector, which dedups this
    // message against RemoteMobManager's state-removal signal and falls back
    // to the DeathSnapshotStore position when the patch got here first (the
    // old findMobPosition-or-bail dropped the beat entirely in that order).
    // The killing-blow school is not on the wire yet (spec §2.2, separate
    // server lane) — the director's school param stays undefined.
    KillBeatDirector.getInstance().onNpcDefeated(data);
  }
}
